import { useTranslation } from 'next-i18next';
import {
  Anchor, bjToast, Button, Spinner,
} from '@components/common';
import {
  FormError, GooglePlacesInput, Input, Label, Textarea,
} from '@components/common/Forms';
import { useAppDispatch, useAppSelector } from '@store/index';
import {
  ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState,
} from 'react';
import { useBjForm } from '@hooks/useBjForm';
import { ApiResumePersonalData, GetPhoneNumberDataApi, isApiV1Errors } from '@utils/data-fetching';
import { SocialItemInput } from '@components/templates/profile/personal-data/SocialItemInput';
import { ResumeSocialIconById } from '@components/templates/profile/common/ResumeSocialIconById';
import { BirthdayInput } from '@components/common/DatepickerInput/BirthdayInput';
import { PhoneInputStandalone } from '@components/common/Forms/PhoneInputStandalone/PhoneInputStandalone';
import { FooterButtonsSaveDelete } from '@components/templates/profile/common/FooterButtonsSaveDelete';
import {
  ArrowDownTrayIcon, EnvelopeOpenIcon, EyeIcon,
} from '@heroicons/react/24/outline';
import { showCvModalTalent } from '@components/templates/cv/cv-in-modal/CvModalTalent';
import { isUserWithoutResume } from '@utils/resume/user-resume';
import { useCvDetails } from '@components/templates/cv/hooks';
import { NextJobsTagCloud } from '@components/templates/profile/personal-data/NextJobsTagCloud';
import { useSwrResumePersonalData } from '@hooks/useSwrApi/endpoints/resume';
import { cn } from '@utils/cn';
import { AiInterviewPreview } from '@components/templates/ai-interview';

// Interface
// ******************************************
interface PersonalDataFormProps {
  locale: Locale;
  showSocialProfiles?: boolean;
  showPrivacyLevel?: boolean;
  isModalLayout?: boolean;
  onRequestUpdatedData: () => void;
  setIsUploadNewCvModalOpen?: (value: boolean) => void;
  userSlug?: string;
  showAboutMe?: boolean;
  showOnlyMissingPersonalFields?: boolean;
}

/**
 * @description PersonalDataForm. Component that renders a form for editing profile personal data, including social profiles and privacy levels.
 *
 * - **locale** -Locale - The current app locale.
 * - **showSocialProfiles** -boolean - Whether the button is disabled or not.
 * - **showPrivacyLevel** -boolean - The navigation direction.
 * - **onRequestUpdatedData** - function - Callback to execute when the data is successfully updated.
 */
export const PersonalDataForm = (props: PersonalDataFormProps) => {
  // Destructure props
  const {
    locale,
    showSocialProfiles = true,
    showPrivacyLevel = true,
    isModalLayout = false,
    onRequestUpdatedData,
    userSlug,
    showAboutMe = true,
    showOnlyMissingPersonalFields = false,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  const dispatch = useAppDispatch();

  const phoneNumber = useAppSelector((state) => state.userOwnResume.phoneNumber);
  const {
    data: personalData,
    mutate: refreshPersonalData,
    isLoading: isLoadingPersonalData,
  } = useSwrResumePersonalData({ locale });

  const userResume = useAppSelector((state) => state.userOwnResume.userResume);
  const hasResume = !isUserWithoutResume(userResume);

  // date state for date picker
  const [date, setDate] = useState<Date | null>(null);

  // states for personal links strings, we use inputs with state for these 3
  const [linkedInProfile, setLinkedInProfile] = useState<string>('');
  const [githubProfile, setGithubProfile] = useState<string>('');
  const [professionalProfile, setProfessionalProfile] = useState<string>('');

  const [socialErrorMessage, setSocialErrorMessage] = useState<string>('');
  const [isLinkedInError, setIsLinkedInError] = useState<boolean>(false);
  const [isGithubError, setIsGithubError] = useState<boolean>(false);
  const [isProfessionalError, setIsProfessionalError] = useState<boolean>(false);

  const socialLinkRef = useRef<HTMLDivElement>({} as HTMLDivElement);

  const [privacyLevel, setPrivacyLevel] = useState<number>(1);
  const [gender, setGender] = useState<string>();
  const [aboutMe, setAboutMe] = useState<string>('');
  const [isGenderError, setIsGenderError] = useState<boolean>(false);

  // A flag indicating whether the form was submitted.
  // It is necessary to prevent useEffect hook from executing more than once after form submission.
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  // A flag indicating whether the phone number is confirmed. We assume it's confirmed and will update later from API.
  const [phoneConfirmed, setPhoneConfirmed] = useState<boolean>(true);
  // Store the initial value of phoneConfirmed to control visibility of the phone input.
  const [initialPhoneConfirmed, setInitialPhoneConfirmed] = useState<boolean | null>(null);

  const { cvDetail } = useCvDetails({
    slug: userSlug,
    toFetchOperationJob: false,
    locale,
    waitForSlug: true,
  });

  const {
    record, checkFormValidation, postFormData, setFormValues, formStatus, isPosting,
  } = useBjForm({
    submitURL: '/v1/resume/personal-data',
  });

  //  populate data based
  useEffect(() => {
    if (personalData) {
      setFormValues(personalData);
      setPrivacyLevel(personalData?.privacyLevel ?? 1);
      if (personalData?.gender) {
        setGender(personalData.gender);
      }
    }
  }, [personalData, setFormValues]);

  // populate local states with data from API
  useEffect(() => {
    if (personalData) {
      if (personalData.birthDate !== undefined) {
        setDate(new Date(personalData.birthDate));
      }
      if (personalData.socialLinks !== undefined) {
        personalData.socialLinks.forEach((socialLink) => {
          if (socialLink.id === 'linkedin') {
            setLinkedInProfile(socialLink.url);
          }
          if (socialLink.id === 'github') {
            setGithubProfile(socialLink.url);
          }
          if (socialLink.id === 'professional') {
            setProfessionalProfile(socialLink.url);
          }
        });
      }
    }
  }, [personalData, setFormValues]);

  // handles form submission response
  useEffect(() => {
    if (!formSubmitted || isPosting) {
      return;
    }

    // form submitted successfully
    if (formStatus.actionStatus === 'success') {
      onRequestUpdatedData();
      void refreshPersonalData();
      bjToast.success(t('global.action.save_successful'));
      setFormSubmitted(false);
    }

    // form submitted with error
    if (formStatus.actionStatus === 'error' && isApiV1Errors(formStatus.data)) {
      bjToast.error(formStatus.data.errors[0].message);

      // scroll to the social links section if there's an error
      if (formStatus.data.errors[0].path === 'socialLinks' && socialLinkRef.current) {
        setSocialErrorMessage(formStatus.data.errors[0].message);
        socialLinkRef.current.scrollIntoView({ behavior: 'smooth' });
      }

      setFormSubmitted(false);
    }
  }, [formStatus, onRequestUpdatedData, refreshPersonalData, t, formSubmitted, isPosting]);

  const onPrivacyLevelPick = (level: number) => {
    setFormValues({ privacyLevel: level });
    setPrivacyLevel(level);
  };

  const onGenderPick = (value: string) => {
    setFormValues({ gender: value });
    setGender(value);
    setIsGenderError(false);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // scroll handled in form
    if (!gender) {
      setIsGenderError(true);
      return;
    }

    // check if social links are valid and stop
    if (isProfessionalError || isGithubError || isLinkedInError) {
      socialLinkRef.current.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // prevent submiting if phone is not confirmed
    if (!phoneConfirmed) return;

    const formData = checkFormValidation(e) as ApiResumePersonalData;
    // Validation failed, return early
    if (typeof formData !== 'object') return;

    postFormData('/v1/resume/personal-data', {
      ...personalData,
      ...formData,
      socialLinks: [
        { id: 'linkedin', url: linkedInProfile },
        { id: 'github', url: githubProfile },
        { id: 'professional', url: professionalProfile },
      ],
      details: aboutMe,
    });
    setFormSubmitted(true);
  };

  const shouldShowLoadingState = isLoadingPersonalData || initialPhoneConfirmed === null;

  // Conditions for field rendering for personal data
  const shouldHideFirstName = shouldShowLoadingState || (showOnlyMissingPersonalFields && personalData?.firstName);
  const shouldHideLastName = shouldShowLoadingState || (showOnlyMissingPersonalFields && personalData?.lastName);
  const shouldHideBirthDate = shouldShowLoadingState || (showOnlyMissingPersonalFields && personalData?.birthDate);
  const shouldHideLocation = shouldShowLoadingState || (showOnlyMissingPersonalFields && personalData?.resumeLocation);
  const shouldHideEmail = shouldShowLoadingState || (showOnlyMissingPersonalFields && personalData?.email);
  const shouldHideGender = shouldShowLoadingState || (showOnlyMissingPersonalFields && personalData?.gender);
  const shouldHidePhone = shouldShowLoadingState || (showOnlyMissingPersonalFields && initialPhoneConfirmed);

  const hiddenPersonalDataFields = [
    shouldHideFirstName,
    shouldHideLastName,
    shouldHideBirthDate,
    shouldHideLocation,
    shouldHideEmail,
    shouldHideGender,
    shouldHidePhone,
  ];

  // Determine if we should show personal data on two columns - if there are at least two fields to show
  const shouldShowPersonalDataOnTwoColumns = hiddenPersonalDataFields.filter((v) => !v).length >= 2;

  const phoneConfirmationCallback = useCallback((confirmed: boolean) => {
    // On first callback, store the initial value
    // When using reactStrictMode useEffect runs twice so first callback will return false because the first request is aborted
    if (initialPhoneConfirmed === null) {
      // initialPhoneConfirmed.current = confirmed;
      setInitialPhoneConfirmed(confirmed);
    }

    setPhoneConfirmed(confirmed);
  }, [initialPhoneConfirmed]);

  const hasCvBasedInterview = userResume?.privateInformation?.interviewVideoStatus === 'completed' && userResume?.privateInformation?.interviewVideos && userResume?.privateInformation?.interviewVideos.length > 0;
  const hasJobBasedInterview = userResume?.privateInformation?.jobInterviewVideos && userResume?.privateInformation?.jobInterviewVideos.length > 0;

  // Render component
  // *****************************************************
  return (
    <form noValidate onSubmit={onSubmit}>
      {/* Personal Data */}
      <div className={cn('grid grid-cols-1 gap-x-4', isModalLayout && shouldShowPersonalDataOnTwoColumns && 'md:grid-cols-2')}>
        {/* Show loading spinner */}
        {shouldShowLoadingState && (
          <div className="p-4">
            <Spinner />
          </div>
        )}

        {/* first name */}
        <Label description={t('global.label.first_name')} required className={cn(shouldHideFirstName && 'hidden')}>
          <Input type="text" {...record('firstName')} minLength={1} required customValidityMessages={{ valueMissing: t('error.message.empty.firstname') }} />
        </Label>

        {/* last name */}
        <Label description={t('global.label.last_name')} required className={cn(shouldHideLastName && 'hidden')}>
          <Input type="text" {...record('lastName')} minLength={1} required customValidityMessages={{ valueMissing: t('error.message.empty.lastname') }} />
        </Label>

        {/* birthday */}
        <div className={cn('flex w-full flex-col', shouldHideBirthDate && 'hidden')}>
          <Label description={t('global.label.birth_date')} htmlFor="birthDate" required />
          <BirthdayInput
            {...record('birthDate')}
            customValidityMessages={{ valueMissing: t('msg-error-birthday-requested') }}
            required
            date={date}
            setDate={(val) => {
              setDate(val);
            }}
          />
        </div>

        {/* location */}
        <Label required htmlFor="resumeLocation" description={t('cv.current.location')} className={cn(shouldHideLocation && 'hidden')}>
          <GooglePlacesInput
            record={record}
            recordName="resumeLocation"
            customValidityMessages={{ valueMissing: t('error.message.empty.location') }}
          />
        </Label>

        {/* email */}
        <Label description={t('global.label.email')} required className={cn(shouldHideEmail && 'hidden')}>
          <Input type="email" {...record('email')} required customValidityMessages={{ valueMissing: t('error.message.empty.email') }} />
        </Label>

        {/* phone input with confirmation component - hidden only with CSS to perform the confirmation in background */}
        <div className={cn('w-full', shouldHidePhone && 'hidden')}>
          <PhoneInputStandalone
            initialValue={phoneNumber}
            toGetDefaultUserPhoneFromApi
            record={record}
            recordName="phone"
            locale={locale}
            onChangeWithSuccess={(phone: string, phoneObject?: GetPhoneNumberDataApi) => {
              dispatch({ type: 'userOwnResume/updateResumePhoneNumber', payload: phone });
              if (phoneObject) {
                dispatch({ type: 'userOwnResume/savePhoneNumber', payload: phoneObject });
              }
            }}
            phoneConfirmedStatusCallback={phoneConfirmationCallback}
          />
        </div>

        {/* gender */}
        <div className={cn('mt-4 w-full sm:w-1/2 sm:pr-7', shouldHideGender && 'hidden')}>
          <Label htmlFor="gender" description={t('global.label.gender')} required />
          <div className="relative mt-2 flex w-fit items-center justify-center rounded-full bg-surface-100 p-2">
            {/* male */}
            <Button
              styling={gender === 'M' ? 'solid' : 'outline'}
              onClick={() => onGenderPick('M')}
              color={isGenderError ? 'error' : 'ink'}
              rounding="none"
              className="mr-0.5 rounded-full border-0 !py-2"
            >
              {t('global.gender.male_long')}
            </Button>
            {/* female */}
            <Button
              styling={gender === 'F' ? 'solid' : 'outline'}
              onClick={() => onGenderPick('F')}
              color={isGenderError ? 'error' : 'ink'}
              rounding="none"
              className="mr-0.5 rounded-full border-0 !py-2"
            >
              {t('global.gender.female_long')}
            </Button>
          </div>
          <Input type="text" required hidden {...record('gender')} />
        </div>
      </div>

      {/* Social profiles */}
      {showSocialProfiles && (
        <div className="mt-6 border-t border-dashed pt-6" ref={socialLinkRef}>
          <h6 className="mb-5 font-semibold">{t('cv.edit.personal_links')}</h6>

          {/* linkedin */}
          <SocialItemInput
            onChange={(e) => {
              setLinkedInProfile(e.target.value);
              setSocialErrorMessage('');
              setIsLinkedInError(!e.target.validity.valid && e.target.value.length > 0);
            }}
            pattern="(.*)linkedin\.com(.*)"
            value={linkedInProfile}
            errorMessage={t('error.message.linkedin_link')}
            placeholder={t('add_linkedin_profile_url')}
            socialName={t('cv.label.social_link.linkedin')}
            socialIcon={ResumeSocialIconById('linkedin', 'w-5 h-5 ')}
          />

          {/* github */}
          <SocialItemInput
            onChange={(e) => {
              setGithubProfile(e.target.value);
              setSocialErrorMessage('');
              setIsGithubError(!e.target.validity.valid && e.target.value.length > 0);
            }}
            pattern="(.*)github\.com(.*)"
            value={githubProfile}
            errorMessage={t('error.message.github_link')}
            placeholder={t('add-github-profile-url')}
            socialName={t('cv.label.social_link.github')}
            socialIcon={ResumeSocialIconById('github', 'w-5 h-5')}
          />

          {/* professional */}
          <SocialItemInput
            onChange={(e) => {
              setProfessionalProfile(e.target.value);
              setSocialErrorMessage('');
              setIsProfessionalError(!e.target.validity.valid && e.target.value.length > 0);
            }}
            pattern="(.*)[A-Za-z0-9]+\.[a-zA-Z]+(.*)"
            value={professionalProfile}
            errorMessage={t('error.message.professional_link')}
            placeholder={t('add_portofolio_url')}
            socialName={t('cv.label.social_link.professional')}
            socialIcon={ResumeSocialIconById('professional', 'w-5 h-5')}
          />

          <FormError errorMessage={socialErrorMessage} isShowing={socialErrorMessage.length > 0} />
        </div>
      )}

      {/* About me */}
      {showAboutMe && (
        <div className="mt-6 border-t border-dashed pt-6">
          <h6 className="mb-4 font-semibold">{t('cv.edit.about-me')}</h6>
          <Textarea
            onChange={(e:ChangeEvent<HTMLTextAreaElement>) => {
              setAboutMe(e.target.value);
            }}
            rows={6}
            {...record('details')}
          />
        </div>
      )}

      {/* Video Interview - Job based and CV based at the end */}
      {(hasCvBasedInterview || hasJobBasedInterview) && (
        <div className="mt-6 border-t border-dashed pt-6">
          <h6 className="mb-4 font-semibold">{t('cv.details.bestie.interviews')}</h6>
          <AiInterviewPreview
            locale={locale}
            interviewVideos={[
              ...(userResume?.privateInformation?.jobInterviewVideos ?? []),
              ...(userResume?.privateInformation?.interviewVideoStatus === 'completed' ? (userResume?.privateInformation?.interviewVideos || []) : []),
            ]}
            requestUpdatedData={onRequestUpdatedData}
            hasDelete
          />
        </div>
      )}

      {/* Next jobs */}
      <NextJobsTagCloud cvDetail={cvDetail} locale={locale} />

      {/* Privacy Level */}
      {showPrivacyLevel && (
        <div className="mt-6 border-t border-dashed pt-6">
          <h6 className="mb-4 font-semibold">{t('cv.edit.profile.options')}</h6>
          <p>{t('cv.status.data.availability.description')}</p>
          <div className="relative mt-2 flex w-fit items-center justify-center rounded-full bg-surface-100 p-2">
            <Button rounding="none" className="mr-0.5 rounded-full border-0 !py-2" styling={privacyLevel === 1 ? 'solid' : 'outline'} color="ink" onClick={() => onPrivacyLevelPick(1)}>{t('privacy_level.label.visible')}</Button>
            <Button rounding="none" className="mr-0.5 rounded-full border-0 !py-2" styling={privacyLevel === 0 ? 'solid' : 'outline'} color="ink" onClick={() => onPrivacyLevelPick(0)}>{t('privacy_level.label.hidden')}</Button>
          </div>
          <div className="mt-5">
            {
                hasResume && (
                <>
                  {/* upload cv */}

                  {/* send on email */}
                  <Anchor className="flex w-full !justify-start gap-3 py-2" styling="text" color="ink" href="app_cv_send_to" symfonyRoute>
                    <EnvelopeOpenIcon className="size-4 text-ink" />
                    <p className="text-sm">{t('cv.action.send')}</p>
                  </Anchor>

                  {/* download */}
                  <Anchor className="flex w-full !justify-start gap-3 py-2" styling="text" color="ink" href="app_cv_download_self" symfonyRoute>
                    <ArrowDownTrayIcon className="size-4 text-ink" />
                    <p className="text-sm">{t('cv.action.pdf')}</p>
                  </Anchor>

                  {/* view cv */}
                  <Button className="flex w-full !justify-start gap-3 py-2" styling="text" color="ink" onClick={() => showCvModalTalent(userResume?.slug ?? '', undefined, true)}>
                    <EyeIcon className="size-4 text-ink" />
                    <span className="text-sm">{t('resume.options.view.candidate')}</span>
                  </Button>


                </>
                )
            }
            {
                !hasResume && (
                <>
                  {/* send on email */}
                  <Button className="flex w-full !justify-start  gap-3 py-2" styling="text" color="ink" disabled>
                    <EnvelopeOpenIcon className="size-4 text-ink" />
                    <p className="text-sm">{t('cv.action.send')}</p>
                  </Button>

                  {/* download */}
                  <Button className="flex w-full !justify-start gap-3 py-2" styling="text" color="ink" disabled>
                    <ArrowDownTrayIcon className="size-4 text-ink" />
                    <p className="text-sm">{t('cv.action.pdf')}</p>
                  </Button>

                  {/* view cv */}
                  <Button className="flex w-full !justify-start gap-3 py-2" styling="text" color="ink" disabled>
                    <EyeIcon className="size-4 text-ink" />
                    <p className="text-sm">{t('resume.options.view.candidate')}</p>
                  </Button>
                </>
                )
            }
          </div>
          <Input type="number" hidden {...record('privacyLevel', { convertValue: 'toNumber', convertEmptyValue: 'toNumber' })} />
        </div>
      )}

      {/* Submit Button */}
      {!shouldShowLoadingState && (
        <FooterButtonsSaveDelete
          isSaveLoading={isPosting}
          className={
            cn(
              'z-1 border-none',
              !isModalLayout && 'fixed inset-x-0 bottom-13 p-4 border-b bg-surface md:static md:bg-transparent md:p-0',
            )
          }
        />
      )}
    </form>
  );
};
